import { FEATURE_PERMISSIONS } from './featurePermissions';
/**
 * Permission utility functions for practice management
 * Based on backend Permission model and PermissionSeeder
 */

// Backend permission mapping - matches the PermissionSeeder exactly
export const BACKEND_PERMISSIONS = {
  ...Object.fromEntries(Object.entries(FEATURE_PERMISSIONS).map(([name, [category, description]]) => [name, { name, category, description }])),
  // Client Management
  VIEW_CLIENTS: { name: 'ViewClients', description: 'Can view client information', category: 'Clients' },
  CREATE_CLIENTS: { name: 'CreateClients', description: 'Can create new clients', category: 'Clients' },
  EDIT_CLIENTS: { name: 'EditClients', description: 'Can edit client information', category: 'Clients' },
  DELETE_CLIENTS: { name: 'DeleteClients', description: 'Can delete clients', category: 'Clients' },
  MANAGE_PHARMACIES: { name: 'ManagePharmacies', description: 'Can manage the organization pharmacy directory', category: 'Clients' },
  
  // Birth Events
  VIEW_BIRTH_EVENTS: { name: 'ViewBirthEvents', description: 'Can view birth events', category: 'BirthEvents' },
  CREATE_BIRTH_EVENTS: { name: 'CreateBirthEvents', description: 'Can create birth events', category: 'BirthEvents' },
  EDIT_BIRTH_EVENTS: { name: 'EditBirthEvents', description: 'Can edit birth events', category: 'BirthEvents' },
  DELETE_BIRTH_EVENTS: { name: 'DeleteBirthEvents', description: 'Can delete birth events', category: 'BirthEvents' },
  
  // Pregnancies
  VIEW_PREGNANCIES: { name: 'ViewPregnancies', description: 'Can view pregnancies', category: 'Pregnancies' },
  CREATE_PREGNANCIES: { name: 'CreatePregnancies', description: 'Can create pregnancies', category: 'Pregnancies' },
  EDIT_PREGNANCIES: { name: 'EditPregnancies', description: 'Can edit pregnancies', category: 'Pregnancies' },
  DELETE_PREGNANCIES: { name: 'DeletePregnancies', description: 'Can delete pregnancies', category: 'Pregnancies' },
  
  // Appointments
  VIEW_APPOINTMENTS: { name: 'ViewAppointments', description: 'Can view appointments', category: 'Appointments' },
  CREATE_APPOINTMENTS: { name: 'CreateAppointments', description: 'Can create appointments', category: 'Appointments' },
  EDIT_APPOINTMENTS: { name: 'EditAppointments', description: 'Can edit appointments', category: 'Appointments' },
  DELETE_APPOINTMENTS: { name: 'DeleteAppointments', description: 'Can delete appointments', category: 'Appointments' },
  
  // Users
  VIEW_USERS: { name: 'ViewUsers', description: 'Can view users in organization', category: 'Users' },
  INVITE_USERS: { name: 'InviteUsers', description: 'Can invite users to organization', category: 'Users' },
  MANAGE_USERS: { name: 'ManageUsers', description: 'Can manage user roles and permissions', category: 'Users' },
  
  // Organization
  MANAGE_ORGANIZATION: { name: 'ManageOrganization', description: 'Can manage organization settings', category: 'Organization' },
  VIEW_ORGANIZATION: { name: 'ViewOrganization', description: 'Can view organization information', category: 'Organization' },
  
  // Messages
  SEND_MESSAGES: { name: 'SendMessages', description: 'Can send messages', category: 'Messages' },
  VIEW_MESSAGES: { name: 'ViewMessages', description: 'Can view messages', category: 'Messages' },
  
  // Chart Entries
  VIEW_CHART_ENTRIES: { name: 'ViewChartEntries', description: 'Can view chart entries', category: 'ChartEntries' },
  CREATE_CHART_ENTRIES: { name: 'CreateChartEntries', description: 'Can create chart entries', category: 'ChartEntries' },
  EDIT_CHART_ENTRIES: { name: 'EditChartEntries', description: 'Can edit chart entries', category: 'ChartEntries' },
  DELETE_CHART_ENTRIES: { name: 'DeleteChartEntries', description: 'Can delete chart entries', category: 'ChartEntries' },
  LOCK_CHARTS: { name: 'LockCharts', description: 'Can sign and lock charts', category: 'Charts' },

  // Documents
  VIEW_FORMS: { name: 'ViewForms', description: 'Can view form templates and form records', category: 'Forms' },
  CREATE_FORMS: { name: 'CreateForms', description: 'Can create forms and form templates', category: 'Forms' },
  EDIT_FORMS: { name: 'EditForms', description: 'Can edit draft forms and form templates', category: 'Forms' },
  LOCK_FORMS: { name: 'LockForms', description: 'Can lock and finalize forms', category: 'Forms' },
  CREATE_FORM_REVISIONS: { name: 'CreateFormRevisions', description: 'Can create new revisions from locked forms', category: 'Forms' },
  PUBLISH_FORMS_TO_PORTAL: { name: 'PublishFormsToPortal', description: 'Can publish forms to the client portal', category: 'Forms' },
  MANAGE_FORM_TEMPLATES: { name: 'ManageFormTemplates', description: 'Can manage form templates and versions', category: 'Forms' },
  MANAGE_CONSENT_TEMPLATES: { name: 'ManageConsentTemplates', description: 'Can create, edit, upload, and archive consent templates', category: 'Consents' },
  MANAGE_HANDOUT_TEMPLATES: { name: 'ManageHandoutTemplates', description: 'Can create, edit, upload, archive, and delete handout templates', category: 'Handouts' },

  // Tasks and chart locking
  VIEW_TASKS: { name: 'ViewTasks', description: 'Can view tasks', category: 'Tasks' },
  CREATE_TASKS: { name: 'CreateTasks', description: 'Can create tasks', category: 'Tasks' },
  EDIT_TASKS: { name: 'EditTasks', description: 'Can edit tasks', category: 'Tasks' },
  DELETE_TASKS: { name: 'DeleteTasks', description: 'Can delete tasks', category: 'Tasks' },
  UNLOCK_CHARTS: { name: 'UnlockCharts', description: 'Can unlock locked charts', category: 'Charts' },

  // Prescriptions
  VIEW_PRESCRIPTIONS: { name: 'ViewPrescriptions', description: 'Can view prescription records', category: 'Prescriptions' },
  CREATE_PRESCRIPTIONS: { name: 'CreatePrescriptions', description: 'Can create and edit prescription drafts', category: 'Prescriptions' },
  SEND_PRESCRIPTIONS: { name: 'SendPrescriptions', description: 'Can sign and fax non-controlled prescriptions', category: 'Prescriptions' },
  SEND_PRESCRIPTIONS_ON_BEHALF: { name: 'SendPrescriptionsOnBehalf', description: 'Can sign and fax for another authorized prescriber', category: 'Prescriptions' },
  VOID_PRESCRIPTIONS: { name: 'VoidPrescriptions', description: 'Can void signed prescription records', category: 'Prescriptions' }
};

// Permission categories matching backend schema
export const PERMISSION_CATEGORIES = {
  Templates: { label: 'Shared Templates', description: 'Manage reusable practice templates and locks', color: 'purple' },
  Billing: { label: 'Billing', description: 'Invoice access, payment management, and settings', color: 'emerald' },
  RevenueCycle: { label: 'Revenue cycle', description: 'Billing handoff, coding review, coverage, and fees', color: 'emerald' },
  Reports: { label: 'Reports', description: 'Run and manage reports', color: 'blue' },
  Fax: { label: 'Fax', description: 'Fax access, sending, inbox, contacts, and lines', color: 'yellow' },
  Clients: {
    label: 'Client Management',
    description: 'Permissions related to client information and care',
    color: 'emerald'
  },
  BirthEvents: {
    label: 'Birth Events',
    description: 'Permissions for managing birth events and newborn information',
    color: 'pink'
  },
  Pregnancies: {
    label: 'Pregnancies',
    description: 'Permissions for managing pregnancy information',
    color: 'purple'
  },
  Appointments: {
    label: 'Appointments',
    description: 'Permissions for managing appointments and scheduling',
    color: 'blue'
  },
  Users: {
    label: 'User Management',
    description: 'Permissions for managing team members and users',
    color: 'indigo'
  },
  Organization: {
    label: 'Organization',
    description: 'Permissions for managing organization settings',
    color: 'red'
  },
  Messages: {
    label: 'Communication',
    description: 'Permissions for messaging and communication features',
    color: 'yellow'
  },
  ChartEntries: {
    label: 'Chart Entries',
    description: 'Permissions for managing medical chart entries',
    color: 'green'
  },
  Forms: { label: 'Forms', description: 'Create, edit, finalize, and publish forms and manage templates', color: 'blue' },
  Consents: { label: 'Consents', description: 'Manage reusable consent templates', color: 'purple' },
  Handouts: { label: 'Handouts', description: 'Manage reusable handout templates', color: 'emerald' },
  Tasks: { label: 'Tasks', description: 'View, create, edit, and delete tasks', color: 'yellow' },
  Charts: { label: 'Chart Locking', description: 'Sign, lock, and unlock charts', color: 'green' },
  Prescriptions: {
    label: 'Prescriptions',
    description: 'Permissions for drafting, signing, faxing, and managing prescriptions',
    color: 'blue'
  }
};

// Default role-based permissions matching backend seeder
export const DEFAULT_ROLE_PERMISSIONS = {
  Admin: [
    'ViewForms', 'CreateForms', 'EditForms', 'LockForms', 'CreateFormRevisions', 'PublishFormsToPortal', 'ManageFormTemplates',
    'ManageConsentTemplates', 'ManageHandoutTemplates', 'ViewTasks', 'CreateTasks', 'EditTasks', 'DeleteTasks', 'UnlockCharts',
    // All permissions (from seeder)
    'ViewClients', 'CreateClients', 'EditClients', 'DeleteClients',
    'ViewBirthEvents', 'CreateBirthEvents', 'EditBirthEvents', 'DeleteBirthEvents',
    'ViewPregnancies', 'CreatePregnancies', 'EditPregnancies', 'DeletePregnancies',
    'ViewAppointments', 'CreateAppointments', 'EditAppointments', 'DeleteAppointments',
    'ViewUsers', 'InviteUsers', 'ManageUsers',
    'ManageOrganization', 'ViewOrganization',
    'SendMessages', 'ViewMessages',
    'ViewChartEntries', 'CreateChartEntries', 'EditChartEntries', 'DeleteChartEntries', 'LockCharts',
    'ViewPrescriptions', 'CreatePrescriptions', 'VoidPrescriptions', 'ManagePharmacies'
  ],
  Midwife: [
    'ViewForms', 'CreateForms', 'EditForms', 'LockForms', 'CreateFormRevisions', 'PublishFormsToPortal', 'ManageFormTemplates',
    'ManageConsentTemplates', 'ManageHandoutTemplates', 'ViewTasks', 'CreateTasks', 'EditTasks',
    // Midwife permissions (from seeder)
    'ViewClients', 'CreateClients', 'EditClients', 'DeleteClients',
    'ViewBirthEvents', 'CreateBirthEvents', 'EditBirthEvents',
    'ViewPregnancies', 'CreatePregnancies', 'EditPregnancies', 'DeletePregnancies',
    'ViewAppointments', 'CreateAppointments', 'EditAppointments',
    'ViewUsers', 'InviteUsers',
    'ViewOrganization',
    'SendMessages', 'ViewMessages',
    'ViewChartEntries', 'CreateChartEntries', 'EditChartEntries', 'LockCharts',
    'ViewPrescriptions', 'CreatePrescriptions', 'VoidPrescriptions', 'ManagePharmacies'
  ],
  Staff: [
    'ViewForms', 'ManageHandoutTemplates', 'ViewTasks', 'CreateTasks',
    // Staff permissions (from seeder)
    'ViewClients',
    'ViewAppointments', 'CreateAppointments', 'EditAppointments',
    'ViewOrganization',
    'SendMessages', 'ViewMessages',
    'ViewChartEntries', 'CreateChartEntries',
    'ViewPrescriptions'
  ],
  Student: [
    // Read-only access — observe clinical workflow without write access
    'ViewClients',
    'ViewAppointments',
    'ViewOrganization',
    'ViewMessages',
    'ViewChartEntries'
  ],
  // Legacy mapping for existing roles
  admin: 'Admin',
  midwife: 'Midwife',
  member: 'Staff',
  student: 'Student'
};

/**
 * Get permissions by category from backend permissions
 */
export const getPermissionsByCategory = (permissions = Object.values(BACKEND_PERMISSIONS)) => {
  const categorized = {};
  const source = Array.isArray(permissions) && permissions.length > 0
    ? permissions
    : Object.values(BACKEND_PERMISSIONS);
  
  source.forEach(permission => {
    if (!categorized[permission.category]) {
      categorized[permission.category] = [];
    }
    categorized[permission.category].push(permission);
  });
  
  return categorized;
};

/**
 * Check if a user has a specific permission
 */
export const hasPermission = (userPermissions, permissionName) => {
  return Array.isArray(userPermissions) && userPermissions.includes(permissionName);
};

/**
 * Get default permissions for a role
 */
export const getDefaultPermissionsForRole = (role) => {
  // Handle legacy role names
  const normalizedRole = DEFAULT_ROLE_PERMISSIONS[role] || role;
  if (typeof normalizedRole === 'string') {
    return DEFAULT_ROLE_PERMISSIONS[normalizedRole] || [];
  }
  return normalizedRole || [];
};

/**
 * Validate permission array
 */
export const validatePermissions = (permissions) => {
  if (!Array.isArray(permissions)) return false;
  
  const validPermissionNames = Object.values(BACKEND_PERMISSIONS).map(p => p.name);
  return permissions.every(permName => validPermissionNames.includes(permName));
};

/**
 * Get permission details by name
 */
export const getPermissionByName = (permissionName) => {
  return Object.values(BACKEND_PERMISSIONS).find(p => p.name === permissionName);
};

/**
 * Format permissions for display
 */
export const formatPermissionsForDisplay = (permissions) => {
  if (!Array.isArray(permissions)) return [];
  
  return permissions.map(permName => {
    const permission = getPermissionByName(permName);
    return permission ? permission.name : permName;
  }).join(', ');
};

/**
 * Get UI-friendly permission name from backend name
 */
export const getUIPermissionName = (permissionName) => {
  // Convert camelCase to readable format
  return permissionName
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
};

/**
 * Map backend permissions to UI format
 */
export const mapBackendPermissionsToUI = (backendPermissions) => {
  return backendPermissions.map(perm => ({
    id: perm.id,
    name: perm.name,
    label: getUIPermissionName(perm.name),
    description: perm.description,
    category: perm.category
  }));
};
